import {
  NJButton,
  NJFormItem,
  NJHeading,
  NJInlineMessage,
  NJMultiSelectItem,
  NJMultiSelectRoot,
  NJSelectItem,
  NJSelectRoot,
  NJSpinner,
  NJText,
} from "@engie-group/fluid-design-system-react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { errorMessage } from "@/api/apiError";
import { BackButton } from "@/components/BackButton";
import {
  type FormErrors,
  isBlank,
  isTooLong,
  isValidUrl,
  lengthHint,
  requiredLabel,
  tooLongMessage,
} from "@/components/form/validation";
import {
  AGENT_MAINTAINER_MAX_LENGTH,
  AGENT_NAME_MAX_LENGTH,
  AGENT_OAUTH_CLIENT_ID_MAX_LENGTH,
  AGENT_SHORT_DESCRIPTION_MAX_LENGTH,
  AGENT_SLUG_MAX_LENGTH,
  AGENT_URL_MAX_LENGTH,
} from "@/features/agents/agentLimits";
import { MarkdownMessage } from "@/features/chat/MarkdownMessage";
import { useAgentUseCases } from "@/features/agents/useAgents";
import type { AdminAgentCreate, AdminAgentUpdate } from "@/types";
import {
  useAdminAgent,
  useCreateAdminAgent,
  useUpdateAdminAgent,
} from "./useAdminAgents";

type AuthType = "NONE" | "API_KEY" | "OAUTH_CLIENT_CREDENTIALS";
type Tab = "general" | "connection";
type FieldKey =
  | "name"
  | "slug"
  | "shortDescription"
  | "maintainer"
  | "contextWindow"
  | "apiEndpoint"
  | "apiKey"
  | "oauthClientId"
  | "oauthClientSecret"
  | "oauthTokenUrl";

type FormState = {
  name: string;
  slug: string;
  shortDescription: string;
  fullDescription: string;
  maintainer: string;
  useCases: string[];
  contextWindow: string;
  status: string;
  apiEndpoint: string;
  authType: AuthType;
  apiKey: string;
  oauthClientId: string;
  oauthClientSecret: string;
  oauthTokenUrl: string;
  oauthScope: string;
};

const INITIAL_STATE: FormState = {
  name: "",
  slug: "",
  shortDescription: "",
  fullDescription: "",
  maintainer: "",
  useCases: [],
  contextWindow: "3000",
  status: "ACTIVE",
  apiEndpoint: "",
  authType: "NONE",
  apiKey: "",
  oauthClientId: "",
  oauthClientSecret: "",
  oauthTokenUrl: "",
  oauthScope: "",
};

const REQUIRED_MESSAGE = "This field is required.";
const INVALID_URL_MESSAGE = "Enter a valid http(s) URL.";

/** Shared admin agent create/edit page (US-11). Two tabs (General / Connection), field-level
 * validation, and a live markdown preview of the full description. Credentials are write-only. */
export function AdminAgentFormPage() {
  const { slug } = useParams<{ slug: string }>();
  const isEdit = Boolean(slug);
  const navigate = useNavigate();

  const { data: agent, isLoading } = useAdminAgent(slug);
  const { data: useCaseOptions } = useAgentUseCases();
  const createAgent = useCreateAdminAgent();
  const updateAgent = useUpdateAdminAgent();

  const [tab, setTab] = useState<Tab>("general");
  const [form, setForm] = useState<FormState>(INITIAL_STATE);
  const [errors, setErrors] = useState<FormErrors<FieldKey>>({});

  useEffect(() => {
    if (agent) {
      setForm({
        name: agent.name,
        slug: agent.slug,
        shortDescription: agent.shortDescription ?? "",
        fullDescription: agent.fullDescription ?? "",
        maintainer: agent.maintainer,
        useCases: agent.useCases ?? [],
        contextWindow: String(agent.contextWindow ?? 3000),
        status: agent.status ?? "ACTIVE",
        apiEndpoint: agent.apiEndpoint ?? "",
        authType: (agent.authType as AuthType) ?? "NONE",
        apiKey: "",
        oauthClientId: "",
        oauthClientSecret: "",
        oauthTokenUrl: agent.oauthTokenUrl ?? "",
        oauthScope: agent.oauthScope ?? "",
      });
    }
  }, [agent]);

  const useCaseChoices = useMemo(
    () =>
      Array.from(new Set([...(useCaseOptions ?? []), ...form.useCases])).sort(
        (a, b) => a.localeCompare(b),
      ),
    [useCaseOptions, form.useCases],
  );

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  function validate(): FormErrors<FieldKey> {
    const next: FormErrors<FieldKey> = {};
    const fail = (field: FieldKey, message: string) => {
      next[field] = message;
    };
    const failIfTooLong = (field: FieldKey, value: string, max: number) => {
      if (!next[field] && isTooLong(value, max))
        fail(field, tooLongMessage(value, max));
    };
    if (isBlank(form.name)) fail("name", REQUIRED_MESSAGE);
    if (isBlank(form.shortDescription))
      fail("shortDescription", REQUIRED_MESSAGE);
    if (isBlank(form.maintainer)) fail("maintainer", REQUIRED_MESSAGE);
    failIfTooLong("name", form.name, AGENT_NAME_MAX_LENGTH);
    if (!isEdit) failIfTooLong("slug", form.slug, AGENT_SLUG_MAX_LENGTH);
    failIfTooLong(
      "shortDescription",
      form.shortDescription,
      AGENT_SHORT_DESCRIPTION_MAX_LENGTH,
    );
    failIfTooLong("maintainer", form.maintainer, AGENT_MAINTAINER_MAX_LENGTH);
    const ctx = Number(form.contextWindow);
    if (!Number.isFinite(ctx) || ctx < 1)
      fail("contextWindow", "Enter a positive number.");
    if (isBlank(form.apiEndpoint)) {
      fail("apiEndpoint", REQUIRED_MESSAGE);
    } else if (!isValidUrl(form.apiEndpoint)) {
      fail("apiEndpoint", INVALID_URL_MESSAGE);
    }
    if (form.authType === "API_KEY" && !isEdit && isBlank(form.apiKey)) {
      fail("apiKey", REQUIRED_MESSAGE);
    }
    if (form.authType === "OAUTH_CLIENT_CREDENTIALS") {
      if (!isEdit && isBlank(form.oauthClientId))
        fail("oauthClientId", REQUIRED_MESSAGE);
      if (!isEdit && isBlank(form.oauthClientSecret))
        fail("oauthClientSecret", REQUIRED_MESSAGE);
      if (isBlank(form.oauthTokenUrl)) {
        fail("oauthTokenUrl", REQUIRED_MESSAGE);
      } else if (!isValidUrl(form.oauthTokenUrl)) {
        fail("oauthTokenUrl", INVALID_URL_MESSAGE);
      }
      failIfTooLong(
        "oauthClientId",
        form.oauthClientId,
        AGENT_OAUTH_CLIENT_ID_MAX_LENGTH,
      );
      failIfTooLong("oauthTokenUrl", form.oauthTokenUrl, AGENT_URL_MAX_LENGTH);
    }
    failIfTooLong("apiEndpoint", form.apiEndpoint, AGENT_URL_MAX_LENGTH);
    return next;
  }

  async function handleSubmit() {
    const found = validate();
    setErrors(found);
    if (Object.keys(found).length > 0) {
      if (
        found.name ||
        found.slug ||
        found.shortDescription ||
        found.maintainer ||
        found.contextWindow
      ) {
        setTab("general");
      } else {
        setTab("connection");
      }
      return;
    }

    const isOauth = form.authType === "OAUTH_CLIENT_CREDENTIALS";
    const credentials = {
      apiKey:
        form.authType === "API_KEY" ? form.apiKey || undefined : undefined,
      oauthClientId: isOauth ? form.oauthClientId || undefined : undefined,
      oauthClientSecret: isOauth
        ? form.oauthClientSecret || undefined
        : undefined,
      oauthTokenUrl: isOauth ? form.oauthTokenUrl || undefined : undefined,
      oauthScope: isOauth ? form.oauthScope || undefined : undefined,
    };

    if (isEdit && slug) {
      const patch: AdminAgentUpdate = {
        name: form.name,
        shortDescription: form.shortDescription,
        fullDescription: form.fullDescription || null,
        maintainer: form.maintainer,
        useCases: form.useCases,
        contextWindow: Number(form.contextWindow),
        status: form.status,
        apiEndpoint: form.apiEndpoint,
        authType: form.authType,
        ...credentials,
      };
      await updateAgent.mutateAsync({ slug, patch });
    } else {
      const input: AdminAgentCreate = {
        name: form.name,
        slug: form.slug.trim() || undefined,
        shortDescription: form.shortDescription,
        fullDescription: form.fullDescription || undefined,
        maintainer: form.maintainer,
        useCases: form.useCases,
        contextWindow: Number(form.contextWindow),
        apiEndpoint: form.apiEndpoint,
        authType: form.authType,
        ...credentials,
      };
      await createAgent.mutateAsync(input);
    }
    navigate("/admin/agents");
  }

  const mutation = isEdit ? updateAgent : createAgent;

  if (isEdit && isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <NJSpinner aria-label="Loading the agent" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <BackButton to="/admin/agents" label="Back to Agents" />
      <NJHeading as="h1" scale="lg">
        {isEdit ? `Edit agent — ${form.name}` : "Create an agent"}
      </NJHeading>

      <div className="flex gap-2 border-b border-nj-neutral-subtle-default">
        <TabButton active={tab === "general"} onClick={() => setTab("general")}>
          General
        </TabButton>
        <TabButton
          active={tab === "connection"}
          onClick={() => setTab("connection")}
        >
          Connection
        </TabButton>
      </div>

      {tab === "general" ? (
        <div className="grid w-full gap-4 sm:grid-cols-2 sm:gap-x-6">
          <NJFormItem
            id="agent-name"
            label="Name"
            isRequired
            hasError={Boolean(errors.name)}
            subscriptMessage={errors.name}
            value={form.name}
            onChange={(e) => set("name", (e.target as HTMLInputElement).value)}
          />
          <NJFormItem
            id="agent-slug"
            label="Slug (generated from the name if left blank)"
            readOnly={isEdit}
            hasError={Boolean(errors.slug)}
            subscriptMessage={errors.slug}
            value={form.slug}
            onChange={(e) => set("slug", (e.target as HTMLInputElement).value)}
          />
          <NJFormItem
            id="agent-short-description"
            label="Short description"
            isRequired
            hasError={Boolean(errors.shortDescription)}
            subscriptMessage={
              errors.shortDescription ??
              lengthHint(
                form.shortDescription,
                AGENT_SHORT_DESCRIPTION_MAX_LENGTH,
              )
            }
            value={form.shortDescription}
            onChange={(e) =>
              set("shortDescription", (e.target as HTMLInputElement).value)
            }
          />
          <NJFormItem
            id="agent-maintainer"
            label="Maintainer"
            isRequired
            hasError={Boolean(errors.maintainer)}
            subscriptMessage={errors.maintainer}
            value={form.maintainer}
            onChange={(e) =>
              set("maintainer", (e.target as HTMLInputElement).value)
            }
          />
          <NJFormItem
            id="agent-context-window"
            label="Context window (tokens)"
            type="number"
            isRequired
            hasError={Boolean(errors.contextWindow)}
            subscriptMessage={errors.contextWindow}
            value={form.contextWindow}
            onChange={(e) =>
              set("contextWindow", (e.target as HTMLInputElement).value)
            }
          />
          {isEdit && (
            <NJSelectRoot
              id="agent-status"
              label="Status"
              value={form.status}
              listNavigationLabel="Use the arrows and Enter to choose the status"
              buttonDefaultValueLabel="Choose a status"
              onChange={(value) => set("status", value ?? "ACTIVE")}
            >
              <NJSelectItem value="ACTIVE">ACTIVE</NJSelectItem>
              <NJSelectItem value="DISABLED">DISABLED</NJSelectItem>
            </NJSelectRoot>
          )}

          <div className="sm:col-span-2">
            <NJMultiSelectRoot
              id="agent-use-cases"
              label="Use cases"
              value={form.useCases}
              placeholder="Select use cases"
              listNavigationLabel="Use arrow keys and Enter to toggle a use case"
              maxTagsToDisplay={5}
              tagColor="brand"
              onChange={(nextValue) => set("useCases", nextValue)}
            >
              {useCaseChoices.map((option) => (
                <NJMultiSelectItem key={option} value={option} label={option}>
                  {option}
                </NJMultiSelectItem>
              ))}
            </NJMultiSelectRoot>
          </div>

          <div className="sm:col-span-2">
            <NJFormItem
              id="agent-full-description"
              label="Full description (markdown)"
              isMultiline
              value={form.fullDescription}
              onChange={(e) =>
                set("fullDescription", (e.target as HTMLTextAreaElement).value)
              }
            />
          </div>
          <div className="sm:col-span-2">
            <PreviewBox markdown={form.fullDescription} />
          </div>
        </div>
      ) : (
        <div className="grid w-full gap-4 sm:grid-cols-2 sm:gap-x-6">
          <div className="sm:col-span-2">
            <NJFormItem
              id="agent-api-endpoint"
              label="API endpoint"
              isRequired
              hasError={Boolean(errors.apiEndpoint)}
              subscriptMessage={errors.apiEndpoint}
              value={form.apiEndpoint}
              onChange={(e) =>
                set("apiEndpoint", (e.target as HTMLInputElement).value)
              }
            />
          </div>
          <div className="sm:col-span-2">
            <NJSelectRoot
              id="agent-auth-type"
              label={requiredLabel("Authentication type")}
              value={form.authType}
              listNavigationLabel="Use the arrows and Enter to choose the authentication type"
              buttonDefaultValueLabel="Choose an authentication type"
              onChange={(value) =>
                set("authType", (value ?? "NONE") as AuthType)
              }
            >
              <NJSelectItem value="NONE">None</NJSelectItem>
              <NJSelectItem value="API_KEY">API key</NJSelectItem>
              <NJSelectItem value="OAUTH_CLIENT_CREDENTIALS">
                OAuth Client Credentials
              </NJSelectItem>
            </NJSelectRoot>
          </div>

          {form.authType === "API_KEY" && (
            <NJFormItem
              id="agent-api-key"
              label={
                isEdit ? "API key (leave blank to keep current)" : "API key"
              }
              type="password"
              isRequired={!isEdit}
              hasError={Boolean(errors.apiKey)}
              subscriptMessage={
                errors.apiKey ??
                (isEdit && agent?.hasApiKey
                  ? "A credential is already configured."
                  : undefined)
              }
              value={form.apiKey}
              onChange={(e) =>
                set("apiKey", (e.target as HTMLInputElement).value)
              }
            />
          )}

          {form.authType === "OAUTH_CLIENT_CREDENTIALS" && (
            <>
              <NJFormItem
                id="agent-oauth-client-id"
                label={
                  isEdit ? "OAuth client ID (blank to keep)" : "OAuth client ID"
                }
                isRequired={!isEdit}
                hasError={Boolean(errors.oauthClientId)}
                subscriptMessage={errors.oauthClientId}
                value={form.oauthClientId}
                onChange={(e) =>
                  set("oauthClientId", (e.target as HTMLInputElement).value)
                }
              />
              <NJFormItem
                id="agent-oauth-client-secret"
                label={
                  isEdit
                    ? "OAuth client secret (blank to keep)"
                    : "OAuth client secret"
                }
                type="password"
                isRequired={!isEdit}
                hasError={Boolean(errors.oauthClientSecret)}
                subscriptMessage={errors.oauthClientSecret}
                value={form.oauthClientSecret}
                onChange={(e) =>
                  set("oauthClientSecret", (e.target as HTMLInputElement).value)
                }
              />
              <NJFormItem
                id="agent-oauth-token-url"
                label="OAuth token URL"
                isRequired
                hasError={Boolean(errors.oauthTokenUrl)}
                subscriptMessage={errors.oauthTokenUrl}
                value={form.oauthTokenUrl}
                onChange={(e) =>
                  set("oauthTokenUrl", (e.target as HTMLInputElement).value)
                }
              />
              <NJFormItem
                id="agent-oauth-scope"
                label="OAuth scope"
                value={form.oauthScope}
                onChange={(e) =>
                  set("oauthScope", (e.target as HTMLInputElement).value)
                }
              />
            </>
          )}
        </div>
      )}

      {mutation.isError && (
        <NJInlineMessage variant="error">
          {errorMessage(
            mutation.error,
            "Could not save this agent (the slug may already be taken).",
          )}
        </NJInlineMessage>
      )}

      <div className="flex gap-3">
        <NJButton
          label={isEdit ? "Save" : "Create agent"}
          disabled={mutation.isPending}
          onClick={() => void handleSubmit()}
        />
        <NJButton
          label="Cancel"
          variant="secondary"
          emphasis="subtle"
          onClick={() => navigate("/admin/agents")}
        />
      </div>
    </div>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`-mb-px border-b-2 px-4 py-2 text-sm font-nj-bold transition ${
        active
          ? "border-nj-brand-solid-default text-nj-brand-solid-default"
          : "border-transparent text-nj-neutral-secondary-default hover:text-nj-neutral-primary-default"
      }`}
    >
      {children}
    </button>
  );
}

function PreviewBox({ markdown }: { markdown: string }) {
  return (
    <div className="flex flex-col gap-1 rounded-lg border border-nj-neutral-subtle-default bg-nj-neutral-secondary-default p-3">
      <NJText
        as="span"
        scale="xs"
        className="font-nj-bold uppercase tracking-wide text-nj-neutral-secondary-default"
      >
        Preview
      </NJText>
      {markdown.trim() ? (
        <MarkdownMessage content={markdown} />
      ) : (
        <NJText
          as="span"
          scale="sm"
          className="text-nj-neutral-tertiary-default"
        >
          No markdown preview yet.
        </NJText>
      )}
    </div>
  );
}
